import { useState } from "react";
import { useLocation } from "react-router";

import ButtonLoading, {
  type ButtonLoadingProps,
} from "~/atoms/buttons/ButtonLoading";
import SubscriptionModalWrapper from "~/components/Stripe/SubscriptionModalWrapper";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import type { Document } from "~/types/collections";
import { type GatedFeature, hasFeatureAccess } from "~/util/documentAccess";

interface GatedButtonProps extends ButtonLoadingProps {
  showModal?: boolean;
  subscriptionPrice?: string;
  returnPath?: string;
  document?: Document;
  documentId?: string | null | undefined; // For use coming from the not logged in/preview flow.
  feature?: GatedFeature;
}

const GatedButton = ({
  onClick,
  showModal = true,
  disabled,
  returnPath,
  document,
  documentId,
  feature,
  ...buttonProps
}: GatedButtonProps) => {
  const {
    data: { organization, role },
  } = useCurrentUser();

  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const currentPath = location.pathname + location.search;

  const isBetaUser = role === "beta-user";

  // If a document is provided, check document-specific access (subscription OR purchase)
  // Otherwise, fall back to subscription-only gating
  const hasAccess = document
    ? hasFeatureAccess(organization, document, isBetaUser, feature)
    : organization?.subscription?.grantsAccess || isBetaUser;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (hasAccess) {
      // User has access, execute the intended action
      onClick?.(event);
    } else if (showModal) {
      // User doesn't have access, show upgrade modal
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <ButtonLoading
        {...buttonProps}
        onClick={handleClick}
        disabled={disabled}
      />
      {showModal && (
        <SubscriptionModalWrapper
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          returnPath={
            returnPath ||
            `/forms/stripe-checkout-return?redirect=${encodeURIComponent(currentPath)}`
          }
          documentId={documentId || document?.id}
        />
      )}
    </>
  );
};

export default GatedButton;
